import { HttpClient } from './httpclient';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import { AxiosRequestConfig } from 'axios';
import {
  PartnerResponseList,
  PartnerResponse,
  Partner,
  PartnerCreateRequest,
} from '@/models/partner';

/**
 * PartnerClient implements the methods to communicate with Partner Service through
 * Management Backend service.
 */
export default class PartnerClient extends HttpClient {
  private static classInstance?: PartnerClient;

  basePath = '/api/v1/partner';
  token = '';
  /**
   *
   * @param baseURL Base url for the client. Defaults to VUE_APP_BACKEND_BASE_URL env var.
   * @param token Authorization token to talk to adapult
   */
  private constructor(
    baseURL = process.env.VUE_APP_BACKEND_BASE_URL,
    token: string
  ) {
    super(baseURL);
    this.token = token;
    if (this.token != '') {
      this._initializeRequestInterceptor();
    }

    this._transformPayload();
  }

  /**
   *
   * @param baseURL Base url for the client. Defaults to VUE_APP_BACKEND_BASE_URL env var.
   * @param token Authorization token to talk to adapult
   * @returns
   */
  public static getInstance(
    baseURL = process.env.VUE_APP_BACKEND_BASE_URL,
    token: string
  ) {
    if (!this.classInstance) {
      this.classInstance = new PartnerClient(baseURL, token);
    }
    return this.classInstance;
  }

  /**
   * Make all requests transform from:
   * snake_case response payload -> camelCase transformation
   * camelCase request payload -> snake_case transformation
   */
  private _transformPayload = () => {
    this.instance.defaults.transformResponse = [
      (data, headers) => {
        if (data && headers) {
          if (
            headers['content-type'].includes('application/json') ||
            headers['Content-Type'].includes('application/json')
          ) {
            return camelcaseKeys(JSON.parse(data), { deep: true });
          }
        }
      },
    ];

    this.instance.defaults.transformRequest = [
      (data, headers) => {
        if (data && headers) {
          if (headers['Content-Type'].includes('application/json')) {
            headers['Content-Type'] = 'application/json';
            return JSON.stringify(snakecaseKeys(data, { deep: true }));
          }
        }
      },
    ];
  };

  /**
   * Makes all requests add the authorization token in the request IF the object
   * was constructed with a non empty token, otherwise ignored.
   */
  private _initializeRequestInterceptor = () => {
    this.instance.interceptors.request.use(
      this._handleRequest,
      this._handleError
    );
  };

  private _handleRequest = (config: AxiosRequestConfig) => {
    if (config.headers) {
      config.headers['Authorization'] = this.token;
    }

    return config;
  };

  /**
   * Makes a call to the backend to retrieve a list of partners
   * @returns Promise to be http response
   */
  public getPartners = (params: URLSearchParams) =>
    this.instance.get<PartnerResponseList>(`${this.basePath}/`, {
      params: params,
    });

  /**
   * postPartner requests the creation of a Partner
   *
   * @param payload data to send in request's body
   * @returns promise with a http response
   */
  public postPartner = (payload: PartnerCreateRequest) =>
    this.instance.post<PartnerResponse>(`${this.basePath}/`, payload);

  /**
   * Makes a call to the backend to retrieve a single partner with an ID
   * @returns Promise to be http response
   */
  public getPartnerById = (id: number) =>
    this.instance.get<Partner>(`${this.basePath}/${id}`);

  /**
   * editPartner requests to edit the content of a Partner
   *
   * @param payload data to send in request's body
   * @returns promise with a http response
   */
  public editPartner = (id: number, params: PartnerCreateRequest) =>
    this.instance.put<Partner>(`${this.basePath}/${id}`, params);

  /**
   * Makes a call to the backend to delete a partner
   * @param id The id of the partner to delete
   * @returns Promise to be handled
   */
  public deletePartner = (id: number) =>
    this.instance.delete(`${this.basePath}/${id}`);
}
